<script setup>
import { useReportMetrics } from 'dashboard/composables/useReportMetrics';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { STATUS } from 'dashboard/store/constants';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  metric: {
    type: Object,
    default: () => ({}),
  },
  accountSummaryKey: {
    type: String,
    default: 'getAccountSummary',
  },
  summaryFetchingKey: {
    type: String,
    default: 'getAccountSummaryFetchingStatus',
  },
});

const { t } = useI18n();

const { calculateTrend, displayMetric, isAverageMetricType, fetchingStatus } =
  useReportMetrics(props.accountSummaryKey, props.summaryFetchingKey);

const trendColor = (value, key) => {
  if (isAverageMetricType(key)) {
    return value > 0
      ? 'border-n-ruby-9 text-n-ruby-9'
      : 'border-auth-accent text-auth-accent';
  }
  return value < 0
    ? 'border-n-ruby-9 text-n-ruby-9'
    : 'border-auth-accent text-auth-accent';
};
</script>

<template>
  <div class="text-auth-muted dark:text-auth-muted-dark">
    <span class="text-xs font-medium uppercase tracking-[0.08em]">
      {{ metric.NAME }}
    </span>
    <div class="mt-1 flex items-end text-auth-text dark:text-auth-text-dark">
      <div v-if="fetchingStatus === STATUS.FETCHING">
        <Spinner />
      </div>
      <div
        v-else-if="fetchingStatus === STATUS.FAILED"
        class="text-n-ruby-10 text-sm"
      >
        {{ t('REPORT.SUMMARY_FETCHING_FAILED') }}
      </div>
      <div
        v-else-if="fetchingStatus === STATUS.FINISHED"
        class="text-2xl font-semibold tabular-nums tracking-[-0.025em]"
      >
        {{ displayMetric(metric.KEY) }}
      </div>
      <div
        v-if="metric.trend && fetchingStatus === STATUS.FINISHED"
        class="mb-0.5 ms-3 flex items-center rounded-md bg-auth-soft px-1.5 py-1 text-xs tabular-nums dark:bg-auth-soft-dark"
      >
        <div
          v-if="metric.trend < 0"
          class="me-1 h-0 w-0 border-x-4 border-t-[8px] border-x-transparent"
          :class="trendColor(metric.trend, metric.KEY)"
        />
        <div
          v-else
          class="me-1 h-0 w-0 border-x-4 border-b-[8px] border-x-transparent"
          :class="trendColor(metric.trend, metric.KEY)"
        />
        <span class="font-medium" :class="trendColor(metric.trend, metric.KEY)">
          {{ calculateTrend(metric.KEY) }}%
        </span>
      </div>
    </div>
  </div>
</template>
